if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      onVariantChangeUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        // Atelier: the size is resolved from the page's own data (snippets/at-variant-data.liquid) in the same frame:
        // the form's variant id, Add to bag or Sold out, the price and Afterpay, the URL, the phone bar and Shopify's
        // product select event. A microtask, so <at-buybox>'s own change handler (which runs after this one, higher up
        // the page) has already noted the pick; nothing paints in between. The section re-render below still runs, in
        // the background, for what only the store knows (live stock, photo order, pick-up), and it
        // only paints if it is the answer to the latest pick.
        const pick = (this.atPick = (this.atPick || 0) + 1);
        const local = shouldSwapProduct ? null : this.findLocalVariant();
        if (local) queueMicrotask(() => pick === this.atPick && this.applyVariantLocally(local, productUrl));

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, local, pick),
          local,
        });
      }

      // Atelier: the page's size data (snippets/at-variant-data.liquid), read once per script element.
      get atVariants() {
        const script = this.querySelector('script[data-at-variants]');
        if (!script) return null;
        if (this.atVariantsScript !== script) {
          this.atVariantsScript = script;
          this.atVariantsData = this.parseJsonScript(this, 'script[data-at-variants]');
        }
        return this.atVariantsData;
      }

      // Atelier: the variant whose option values are the ones now checked (pills, swatches) or selected (dropdowns),
      // in option order; null when an option has no choice yet or the page has no size data.
      findLocalVariant() {
        const data = this.atVariants;
        const selects = this.variantSelectors;
        if (!data || !Array.isArray(data.variants) || !selects) return null;
        const values = Array.from(selects.querySelectorAll('fieldset, .product-form__input--dropdown'), (group) => {
          const input = group.querySelector('input:checked');
          if (input) return input.value;
          const select = group.querySelector('select');
          return select && select.selectedOptions.length ? select.selectedOptions[0].value : null;
        });
        if (!values.length || values.some((value) => value == null)) return null;
        return (
          data.variants.find(
            (variant) =>
              Array.isArray(variant.options) &&
              variant.options.length === values.length &&
              variant.options.every((option, i) => option === values[i])
          ) || null
        );
      }

      // Atelier: what the section re-render would show for this variant, applied now (see handleOptionValueChange).
      applyVariantLocally(variant, productUrl) {
        const selects = this.variantSelectors;
        // The checked pills become the page's own (the markup the re-render brings): <at-buybox> treats a checked pill
        // without it as a pick whose variant has not arrived and holds Add to bag for it.
        selects?.querySelectorAll('input[type=radio]').forEach((input) => {
          if (input.defaultChecked !== input.checked) input.defaultChecked = input.checked;
        });
        selects?.querySelectorAll('.is-unsized').forEach((group) => group.classList.remove('is-unsized'));
        if (selects) selects.dataset.selectedPriceAmount = variant.amount ?? '';
        this.updateVariantInputs(variant.id);
        this.updateURL(productUrl, variant.id);

        const price = this.querySelector(`#price-${this.dataset.section}`);
        const priceHtml = this.atVariants?.prices?.[variant.price];
        if (price && typeof priceHtml === 'string' && !this.sameHtml(price.innerHTML, priceHtml)) price.innerHTML = priceHtml;

        if (variant.media) {
          const mediaId = `${this.dataset.section}-${variant.media}`;
          const gallery = this.querySelector('media-gallery');
          if (gallery?.querySelector(`[data-media-id="${mediaId}"]`)) {
            this.atLocalMedia = { pick: this.atPick, id: gallery.setActiveMedia?.(mediaId, true) };
          }
        }

        // Shopify's product select event (VariantSelects.dispatchProductSelectEvent) resolves with this variant now; the
        // re-render finds the promise already taken.
        selects?.resolvePendingSelectPromise(
          { id: variant.id, title: variant.title, available: variant.available },
          { dataset: { selectedPriceAmount: variant.amount, currencyCode: selects.dataset.currencyCode } }
        );

        // Last: its at:submit-state lets <at-buybox> settle the phone bar and make an Add to bag queued for this size.
        this.productForm?.toggleSubmitButton(!variant.sellable, window.variantStrings.soldOut);

        publish(PUB_SUB_EVENTS.variantChange, {
          data: { sectionId: this.sectionId, html: null, variant, local: true },
        });
      }

      sameHtml(a, b) {
        return String(a).replace(/\s+/g, ' ').trim() === String(b).replace(/\s+/g, ' ').trim();
      }

      resetProductFormState() {
        const productForm = this.productForm;
        // Atelier: on the product page the button stays enabled while the size re-renders. A tap on it then is held by
        // <at-buybox> (assets/at-product.js: the checked size is not yet the form's, so it queues the add and makes it
        // once the size's variant id has arrived). Disabled, as in Dawn, a quick "size, Add to bag" was simply lost.
        if (!this.querySelector('at-buybox')) productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback, local = null }, retried = false) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        // Atelier: an answer without the size picker (an empty 404, a cut-off page) counts as a failure, like a
        // network error, and either is tried once more straight away before the size is called "Unavailable": a
        // single dropped request no longer tells the shopper an in-stock size cannot be bought. A second failure
        // shows "Unavailable" (the phone bar mirrors it) and choosing the size again retries. No timers.
        fetch(requestUrl, { signal })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (!html.querySelector('variant-selects')) throw new Error('No size picker in the answer');
            return html;
          })
          .then(
            (html) => {
              this.pendingRequestUrl = null;
              // Atelier: focus goes back to the chosen size only if it was in the size picker (the keyboard, or a
              // browser that focuses a tapped pill), and without scrolling. Safari never focuses a tapped pill, so
              // this moved focus there and pulled the page back up to the sizes if she had scrolled on (WebKit
              // scrolled to it even with preventScroll).
              const hadFocus = !!this.variantSelectors?.contains(document.activeElement);
              callback(html);
              // set focus to last clicked option value
              if (hadFocus) document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
            },
            (error) => {
              if (error.name === 'AbortError' || signal.aborted) {
                console.log('Fetch aborted by user');
                this.variantSelectors?.rejectPendingSelectPromise(error);
                return;
              }
              if (!retried) {
                this.renderProductInfo({ requestUrl, targetId, callback, local }, true);
                return;
              }
              throw error;
            }
          )
          .catch((error) => {
            console.error(error);
            // Atelier: the size re-render never arrived (twice). A size resolved from the page's own data is already
            // complete (id, Add to bag, price), so nothing changes. Otherwise the form still holds the previous size's
            // variant id: say "Unavailable" rather than leave a disabled Add to bag (the phone bar mirrors it).
            // Choosing the size again retries.
            if (local) return;
            this.setUnavailable();
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      // Atelier: local is the variant already applied from the page's data (applyVariantLocally), pick the number of
      // the size pick this answer belongs to. An answer for an older pick never paints. When the store agrees with the
      // local variant, only what differs is touched: the size pills stay (re-rendered, they were replaced under her
      // finger a second after the tap) unless another option's availability depends on this one, the price block is
      // replaced only if it differs (its role=status would read the same price again), and the button only if the
      // store now says otherwise (stock changed since the page loaded). The store's answer always wins.
      handleUpdateProductInfo(productUrl, local = null, pick = this.atPick) {
        return (html) => {
          if (pick !== this.atPick) return;
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);
          const agrees = !!local && !!variant && String(variant.id) === String(local.id);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          if (!agrees || this.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input--dropdown').length > 1) {
            this.updateOptionValues(html);
          }
          this.updateURL(productUrl, variant?.id);
          if (!agrees) this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          const mediaId = variant.featured_media?.id;
          const mediaApplied =
            agrees && this.atLocalMedia?.pick === pick &&
            this.atLocalMedia.id === `${this.dataset.section}-${mediaId}`;
          this.updateMedia(html, mediaId, !mediaApplied);

          const price = html.getElementById(`price-${this.sectionId}`);
          const currentPrice = this.querySelector(`#price-${this.dataset.section}`);
          if (price && currentPrice) {
            if (!this.sameHtml(currentPrice.innerHTML, price.innerHTML)) currentPrice.innerHTML = price.innerHTML;
            currentPrice.classList.remove('hidden');
          }

          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const serverDisabled = html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true;
          if (!agrees || serverDisabled !== !local.sellable) {
            this.productForm?.toggleSubmitButton(serverDisabled, window.variantStrings.soldOut);
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        // Atelier: the price stays (every product has one option, so the product's price still applies); hiding it
        // pulled Afterpay, the rating and the sizes up by the price's height (layout shift 0.013 at 1440).
        this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.add('hidden');
      }

      updateMedia(html, variantFeaturedMediaId, activate = true) {
        if (!variantFeaturedMediaId) return;

        // Atelier: slides and thumbnails follow the store's render together (media-gallery.js reconcile); Dawn synced the
        // slides only, so with hide_variants a size's own photo had no thumbnail and setActiveMedia threw.
        const gallery = this.querySelector('media-gallery');
        gallery?.reconcile?.(html.querySelector('media-gallery'), !activate);
        if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);

        // Atelier: an agreeing answer must not undo a swipe or scroll since the instant size pick. Reconcile still
        // runs for hide_variants; only a corrected photo, or one that could not be selected locally, activates here.
        if (activate) gallery?.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      // Atelier: the product's own form (#product-form-<section>), never a "Pairs well with" row's product-form, which
      // can come first in the buy box when its block is moved above the buy buttons.
      get productForm() {
        return (
          this.querySelector(`#product-form-${this.dataset.section}`)?.closest('product-form') ||
          this.querySelector('product-form:not(.at-pair__form)')
        );
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
